import React from 'react';
import { FileText, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/routes/paths';
import { formatINR } from '@/shared/utils/currency';
import type { QuotationItem } from './types';

interface QuotationCommandWidgetProps {
  quotations: QuotationItem[];
}

export const QuotationCommandWidget: React.FC<QuotationCommandWidgetProps> = ({ quotations }) => {
  const navigate = useNavigate();

  const openCount = quotations.filter((q) => q.status !== 'converted' && q.status !== 'rejected').length;
  const expiringCount = quotations.filter((q) => q.status === 'expiring_soon').length;
  const totalValue = quotations.reduce((s, q) => s + q.amount, 0);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'sent':
        return 'bg-primary-container text-primary-dim border-primary/30';
      case 'awaiting_response':
        return 'bg-info/10 text-info-text border-info/30';
      case 'converted':
        return 'bg-success/10 text-success-text border-success/30';
      case 'expiring_soon':
        return 'bg-danger/5 text-danger-text border-danger/30';
      default:
        return 'bg-surface-variant text-on-surface-variant border-border';
    }
  };

  return (
    <div className="flex h-full flex-col justify-between rounded-card border border-border bg-card p-5 shadow-xs">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between border-b border-outline/80 pb-3">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-container text-primary">
              <FileText className="h-4 w-4" />
            </div>
            <div>
              <span className="text-body-sm font-semibold uppercase tracking-wider text-on-surface">
                Quotation Overview
              </span>
              <div className="text-data-xs text-on-surface-subtle">Active quotes & closing pipeline</div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => navigate(ROUTES.quotations)}
            className="inline-flex items-center gap-1 text-data-xs font-semibold text-primary hover:underline"
          >
            <span>View All</span>
            <ChevronRight className="h-3 w-3" />
          </button>
        </div>

        {/* Counter Pills */}
        <div className="my-3 grid grid-cols-3 gap-2">
          <div className="rounded-lg border border-border bg-surface-variant/70 p-2 text-center">
            <span className="text-data-xs font-medium text-on-surface-variant">Open Quotes</span>
            <div className="font-semibold text-body-sm text-on-surface">{openCount}</div>
          </div>
          <div className="rounded-lg border border-info/30 bg-info/10 p-2 text-center">
            <span className="text-data-xs font-medium text-info-text">Shown</span>
            <div className="font-semibold text-body-sm text-info-text">{quotations.length}</div>
          </div>
          <div className="rounded-lg border border-danger/30 bg-danger/5 p-2 text-center">
            <span className="text-data-xs font-medium text-danger-text">Expiring</span>
            <div className="font-semibold text-body-sm text-danger-text">{expiringCount}</div>
          </div>
        </div>

        {/* Actionable Quotes Table */}
        {quotations.length === 0 ? (
          <div className="my-6 text-center text-body-sm text-on-surface-subtle">No quotations yet.</div>
        ) : (
        <div className="my-2 flex flex-col divide-y divide-border">
          {quotations.map((q) => (
            <div
              key={q.id}
              onClick={() => navigate(ROUTES.quotationDetail.replace(':id', q.id))}
              className="group flex cursor-pointer items-center justify-between py-2.5 transition hover:bg-surface-variant px-1 rounded-lg"
            >
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-body-sm text-on-surface group-hover:text-primary transition">
                    {q.quoteNumber}
                  </span>
                  <span className="text-on-surface-subtle">•</span>
                  <span className="text-body-sm font-semibold text-on-surface">{q.client}</span>
                </div>
                <div className="mt-0.5 flex items-center gap-2 text-data-xs text-on-surface-subtle">
                  <span className="font-semibold text-on-surface">{formatINR(q.amount)}</span>
                  <span>•</span>
                  <span
                    className={`inline-block rounded-md border px-1.5 py-0.5 text-data-xs font-semibold ${getStatusBadge(q.status)}`}
                  >
                    {q.statusLabel}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-data-xs font-semibold text-primary">{q.nextAction}</span>
                <ChevronRight className="h-3.5 w-3.5 text-on-surface-subtle group-hover:text-primary transition" />
              </div>
            </div>
          ))}
        </div>
        )}
      </div>

      {/* Footer */}
      <div className="rounded-lg border border-border bg-surface-variant/70 p-3 flex items-center justify-between text-body-sm">
        <span className="text-on-surface-subtle">Quotation Value Shown:</span>
        <span className="font-semibold text-on-surface">{formatINR(totalValue)}</span>
      </div>
    </div>
  );
};
